import React, { useEffect, useState } from "react";
import { SwissDateField } from "@/components/SwissDateInput";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { apiClient } from "@/lib/apiClient";

const EMPTY = { title: "", partner: "", amount: "", interval: "monthly", currency: "CHF", start_date: "", expense_account: "" };
const sel = "h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm";

export function AddSubscriptionDialog({ open, onOpenChange, companyId, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [accounts, setAccounts] = useState([]);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    if (!open || !companyId) return;
    setForm(EMPTY);
    apiClient.get(`/companies/${companyId}/accounts`).then((r) => setAccounts((r.data || []).filter((a) => a.type === "expense"))).catch(() => {});
  }, [open, companyId]);

  const save = async () => {
    if (!form.title.trim() || !(Number(form.amount) > 0)) { toast.error("Bitte Bezeichnung und Betrag angeben."); return; }
    setSaving(true);
    try {
      await apiClient.post(`/companies/${companyId}/contracts`, {
        title: form.title.trim(), partner: form.partner.trim() || form.title.trim(), contract_type: "abo", amount: Number(form.amount), interval: form.interval,
        currency: form.currency, start_date: form.start_date || null, expense_account: form.expense_account || null, auto_book_bank: !!form.expense_account, notice_period_months: 1,
      });
      toast.success(`Abo «${form.title.trim()}» erfasst.`);
      onOpenChange(false);
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Abo konnte nicht gespeichert werden."); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="add-subscription-dialog">
        <DialogHeader><DialogTitle>Abo manuell erfassen</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <div><label className="text-xs text-slate-600">Bezeichnung *</label><Input value={form.title} onChange={set("title")} placeholder="z.B. Adobe Creative Cloud" data-testid="sub-input-title" /></div>
          <div><label className="text-xs text-slate-600">Anbieter</label><Input value={form.partner} onChange={set("partner")} placeholder="z.B. Adobe Systems" data-testid="sub-input-partner" /></div>
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-1"><label className="text-xs text-slate-600">Betrag *</label><Input type="number" step="0.01" min="0" value={form.amount} onChange={set("amount")} data-testid="sub-input-amount" /></div>
            <div><label className="text-xs text-slate-600">Währung</label>
              <select value={form.currency} onChange={set("currency")} className={sel} data-testid="sub-input-currency">{["CHF", "EUR", "USD", "GBP"].map((c) => <option key={c}>{c}</option>)}</select></div>
            <div><label className="text-xs text-slate-600">Zyklus</label>
              <select value={form.interval} onChange={set("interval")} className={sel} data-testid="sub-input-interval">
                <option value="weekly">wöchentlich</option><option value="monthly">monatlich</option><option value="quarterly">quartalsweise</option><option value="yearly">jährlich</option>
              </select></div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><label className="text-xs text-slate-600">Letzte / erste Abbuchung</label><SwissDateField  value={form.start_date} onChange={set("start_date")} data-testid="sub-input-start" /></div>
            <div><label className="text-xs text-slate-600">Aufwandskonto</label>
              <select value={form.expense_account} onChange={set("expense_account")} className={sel} data-testid="sub-input-account">
                <option value="">– keines –</option>{accounts.map((a) => <option key={a.code} value={a.code}>{a.code} {a.name}</option>)}
              </select></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="sub-cancel-btn">Abbrechen</Button>
          <Button onClick={save} disabled={saving} data-testid="sub-save-btn">{saving ? "Speichern …" : "Abo speichern"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
